import { cn } from '@/utils/cn';
import { formatScore } from '@/utils/formatters';
import { getScoreColor } from '@/utils/colors';

interface ScoreDisplayProps {
  score: number;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  showPercentage?: boolean;
  className?: string;
}

function ScoreDisplay({
  score,
  size = 'md',
  label = 'Score',
  showPercentage = true,
  className,
}: ScoreDisplayProps) {
  const percentage = score * 100;
  const color = getScoreColor(score);
  const radius = size === 'sm' ? 40 : size === 'md' ? 60 : 80;
  const strokeWidth = size === 'sm' ? 6 : size === 'md' ? 8 : 10;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <div className="relative">
        <svg height={radius * 2} width={radius * 2}>
          {/* Background circle */}
          <circle
            stroke="var(--color-border-subtle)"
            fill="transparent"
            strokeWidth={strokeWidth}
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
          {/* Progress circle */}
          <circle
            stroke={color}
            fill="transparent"
            strokeWidth={strokeWidth}
            strokeDasharray={`${circumference} ${circumference}`}
            style={{
              strokeDashoffset,
              transition: 'stroke-dashoffset 0.5s ease',
              transform: 'rotate(-90deg)',
              transformOrigin: '50% 50%',
            }}
            strokeLinecap="round"
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className={cn('font-bold', {
              'text-xl': size === 'sm',
              'text-3xl': size === 'md',
              'text-4xl': size === 'lg',
            })}
            style={{ color }}
          >
            {showPercentage ? formatScore(score, 0) : score.toFixed(2)}
          </span>
        </div>
      </div>
      <p className="mt-2 text-sm text-text-secondary font-medium">{label}</p>
    </div>
  );
}

export default ScoreDisplay;
